import { Container } from "reactstrap";
import { lazy, Suspense } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

import { useHistory } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import "./homebanner.scss";

// Pre-import only first few critical images for speed
import desktopImg1 from "../../../assets/images/h-banner-desktop/ban-desktop-image-1.jpg";
import desktopImg2 from "../../../assets/images/h-banner-desktop/ban-desktop-image-2.jpg";
import desktopImg3 from "../../../assets/images/h-banner-desktop/ban-desktop-image-3.jpg";
import mobileImg1 from "../../../assets/images/h-banner-mobile/ban-mobile-image-1.jpg";
import mobileImg2 from "../../../assets/images/h-banner-mobile/ban-mobile-image-2.jpg";
import mobileImg3 from "../../../assets/images/h-banner-mobile/ban-mobile-image-3.jpg";

// Import WebP versions for better compression (where beneficial)
import mobileImg2WebP from "../../../assets/images/h-banner-mobile/ban-mobile-image-2.webp";
import mobileImg3WebP from "../../../assets/images/h-banner-mobile/ban-mobile-image-3.webp";

// Lazy load modal only when needed
const Modal = lazy(() =>
  import("reactstrap").then((module) => ({ default: module.Modal }))
);

// WebP support detection
const supportsWebP = (() => {
  if (typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
})();

// Fast slide component using pre-imported images
const LazySlide = ({ imageType, index, isActive }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    // Use pre-imported images for first 3 slides (instant loading)
    const isDesktop = imageType.includes("desktop");

    if (index === 0) {
      setImageUrl(isDesktop ? desktopImg1 : mobileImg1);
    } else if (index === 1) {
      // Use WebP for mobile image 2 if supported (18KB savings)
      setImageUrl(
        isDesktop ? desktopImg2 : supportsWebP ? mobileImg2WebP : mobileImg2
      );
    } else if (index === 2) {
      // Use WebP for mobile image 3 if supported (4KB savings)
      setImageUrl(
        isDesktop ? desktopImg3 : supportsWebP ? mobileImg3WebP : mobileImg3
      );
    } else if (isActive && !imageUrl) {
      // Only load remaining images when they become active
      const imageNumber = index + 1;
      import(`../../../assets/images/${imageType}-${imageNumber}.jpg`)
        .then((module) => {
          setImageUrl(module.default);
        })
        .catch((err) => {
          console.warn(`Failed to load banner ${imageNumber}`, err);
        });
    }
  }, [imageType, index, isActive, imageUrl]);

  return (
    <section
      className="h-banner"
      style={{
        backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "400px",
        backgroundColor: "#f0f0f0",
      }}
    >
      <Container></Container>
    </section>
  );
};

// Optimized Swiper carousel with lazy loading
const OptimizedCarousel = ({ isMobile }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const imageCount = isMobile ? 12 : 11;
  const imageType = isMobile
    ? "h-banner-mobile/ban-mobile-image"
    : "h-banner-desktop/ban-desktop-image";

  return (
    <Swiper
      modules={[Autoplay, Pagination]}
      spaceBetween={0}
      slidesPerView={1}
      autoplay={{
        delay: 10000,
        disableOnInteraction: false,
      }}
      pagination={{
        clickable: true,
        dynamicBullets: true,
      }}
      speed={1000}
      loop={true}
      onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
      className="swiper-banner"
    >
      {Array.from({ length: imageCount }, (_, index) => (
        <SwiperSlide key={index}>
          <LazySlide
            imageType={imageType}
            index={index}
            isActive={activeIndex === index}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const HomeBanner = (props) => {
  const { data, homeData } = props;
  const [showAd, setShowAd] = useState(false);
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 767);

  // Progressive image preloading after initial render
  useEffect(() => {
    const preloadRemainingImages = () => {
      const imageType = isMobile
        ? "h-banner-mobile/ban-mobile-image"
        : "h-banner-desktop/ban-desktop-image";
      const imageCount = isMobile ? 12 : 11;

      // Preload images 2-4 with high priority (likely to be seen next)
      for (let i = 2; i <= Math.min(4, imageCount); i++) {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "image";
        link.href = require(`../../../assets/images/${imageType}-${i}.jpg`);
        document.head.appendChild(link);
      }

      // Preload remaining images with low priority during idle time
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => {
          for (let i = 5; i <= imageCount; i++) {
            try {
              const link = document.createElement("link");
              link.rel = "prefetch";
              link.as = "image";
              link.href = require(`../../../assets/images/${imageType}-${i}.jpg`);
              document.head.appendChild(link);
            } catch (e) {
              // Silently fail for missing images
            }
          }
        });
      }
    };

    // Start preloading after a short delay to not block initial render
    const timeoutId = setTimeout(preloadRemainingImages, 1000);
    return () => clearTimeout(timeoutId);
  }, [isMobile]);

  // Debounced resize handler for better performance
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth < 767;
        if (mobile !== isMobile) {
          setIsMobile(mobile);
        }
      }, 150);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile]);

  const openAd = useCallback(() => setShowAd(true), []);
  const closeAd = useCallback(() => setShowAd(false), []);

  return (
    <div className="home-banner">
      <OptimizedCarousel isMobile={isMobile} />

      <div className="h-content">
        <div />
        <div className="bannerText">
          <h2>
            Discover the best of Sharjah's sights, activities, culture and more
          </h2>
        </div>
      </div>

      <div
        className="container-fluid py-2 text-light text-center ad"
        style={{ backgroundColor: "black", cursor: "pointer", color: "white" }}
        onClick={openAd}
      >
        <div className="promotion-welcome h3">
          Get 10% off your first order with promo{" "}
          <span className="bg-warning px-2">WELCOME10</span>
        </div>
      </div>

      <div
        className="container-fluid py-2 text-light text-center ad d-none"
        style={{
          backgroundColor: "lightblue",
          cursor: "pointer",
          color: "white",
        }}
        onClick={() => history.push("/upload")}
      >
        <div className="promotion-welcome">
          Spend AED 50 and stand a chance to win up to
          <span className="bg-warning px-2" style={{ color: "#eb6139" }}>
            AED15000
          </span>
        </div>
      </div>

      {showAd && (
        <Suspense fallback={<div />}>
          <Modal
            isOpen={showAd}
            centered
            keyboard
            backdrop={false}
            size="lg"
            scrollable={false}
            toggle={closeAd}
            onOpened={() => {
              document.body.style.overflow = "hidden";
            }}
            onClosed={() => {
              document.body.style.overflow = "auto";
            }}
          >
            <div
              className="ad-modal text-center text-uppercase d-flex flex-column align-items-center position-relative"
              style={{ gap: "15px" }}
            >
              <div className="close-icon">
                <button
                  type="button"
                  className="close"
                  onClick={closeAd}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <Suspense fallback={<div>Loading...</div>}>
                <img
                  src={require("../../../assets/images/ad-popup.png")}
                  alt="Promotion"
                  className="w-100"
                  loading="lazy"
                />
              </Suspense>
            </div>
          </Modal>
        </Suspense>
      )}
    </div>
  );
};

export default HomeBanner;
